import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import { relativeAgo } from "@/lib/presence";

interface ActorRef {
  id: string | null;
  name: string | null;
  role: string | null;
  online: boolean;
}
interface AuditRow {
  id: string;
  created_at: string;
  actor: ActorRef;
  action: string;
  category: string;
  method: string | null;
  path: string | null;
  status_code: number | null;
  ip: string | null;
}
interface AuditList {
  items: AuditRow[];
  total: number;
}

const PAGE_SIZE = 50;

function catClass(cat: string): string {
  if (cat === "admin") return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  if (cat === "auth") return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (cat === "write") return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  return "bg-white/5 text-slate-400 border-white/10";
}

/** The raw HTTP request log — every authenticated API call, reads included.
 *
 *  Kept for real investigations, but demoted behind a disclosure: two thirds of
 *  it is page polling, its `category` is derived from the HTTP verb (so POST
 *  searches read as writes), and 99% of rows carry no entity id. The tabs above
 *  answer "what did the team do"; this answers "what exactly hit the API".
 *
 *  Fetches lazily — nothing is requested until the disclosure is opened. */
export function RawRequestLog({ open }: { open: boolean }) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (category) p.set("category", category);
    if (search) p.set("q", search);
    return p.toString();
  }, [offset, category, search]);

  const load = useCallback(async () => {
    const s = ++seq.current;
    setLoading(true);
    try {
      const r = await api<AuditList>(`/api/audit?${qs}`);
      if (s !== seq.current) return;
      setRows(r.items);
      setTotal(r.total);
    } finally {
      if (s === seq.current) setLoading(false);
    }
  }, [qs]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={category}
          onChange={(e) => {
            setOffset(0);
            setCategory(e.target.value);
          }}
          className="rounded-lg bg-ink-900 border border-white/10 text-sm text-slate-200 px-3 py-2"
        >
          <option value="">All types</option>
          <option value="read">Reads</option>
          <option value="write">Writes</option>
          <option value="admin">Admin</option>
          <option value="auth">Auth</option>
        </select>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setOffset(0);
            setSearch(searchInput.trim());
          }}
          className="flex-1 min-w-[180px]"
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search action or path…"
            className="w-full rounded-lg bg-ink-900 border border-white/10 text-sm text-slate-200 px-3 py-2 placeholder:text-slate-500"
          />
        </form>
      </div>

      <div className="rounded-lg border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="text-left font-medium px-3 py-2">When</th>
                <th className="text-left font-medium px-3 py-2">User</th>
                <th className="text-left font-medium px-3 py-2">Action</th>
                <th className="text-left font-medium px-3 py-2 hidden md:table-cell">Path</th>
                <th className="text-left font-medium px-3 py-2 hidden lg:table-cell">IP</th>
                <th className="text-right font-medium px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="px-3 py-2.5" colSpan={6}>
                        <Skeleton className="h-4 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap" title={r.created_at}>
                        {relativeAgo(r.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar name={r.actor.name || "?"} size="xs" />
                          <span className="text-slate-300 truncate max-w-[140px]">
                            {r.actor.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] uppercase ${catClass(r.category)}`}>
                            {r.category}
                          </span>
                          <span className="text-slate-300 font-mono text-[12px]">{r.action}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-[11px] hidden md:table-cell truncate max-w-[280px]" title={r.path || ""}>
                        <span className="text-slate-600">{r.method}</span> {r.path}
                      </td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-[11px] hidden lg:table-cell">
                        {r.ip || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={r.status_code && r.status_code >= 400 ? "text-rose-300" : "text-slate-500"}>
                          {r.status_code ?? "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                    Nothing matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-white/5 text-xs text-slate-500">
          <span>
            {total > 0 ? `${offset + 1}–${offset + rows.length} of ${total}` : "0 events"}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="px-2.5 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"
            >
              Prev
            </button>
            <button
              disabled={offset + rows.length >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="px-2.5 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
